import { Container as InversifyContainer } from 'inversify';

import { FrozenParameterBag } from './frozen-parameter-bag';
import { ParameterBag } from './parameter-bag';
import { ContainerInterface, InjectionToken } from '../../interfaces/components/dependency-injection/container.interface';
import { ParameterBagInterface } from '../../interfaces/components/dependency-injection/parameter-bag.interface';
import { Newable } from '../../interfaces/newable.interface';

export class Container implements ContainerInterface {
  protected readonly container: InversifyContainer;
  private compiled = false;
  private parameterBag: ParameterBagInterface;

  constructor (parameterBag?: ParameterBagInterface) {
    this.parameterBag = parameterBag ?? new ParameterBag();
    this.container = new InversifyContainer({
      defaultScope: 'Singleton',
    });
  }

  public set<T> (id: InjectionToken<T>, service: Newable<T> | object): void {
    if (this.container.isBound(id)) {
      throw new Error(`The "${String(id)}" service is already initialized, you cannot replace it.`);
    }

    if (typeof service === 'object') {
      this.container.bind(id).toConstantValue(service);
    } else {
      this.container.bind(id).to(service);
    }
  }

  public alias<T> (id: InjectionToken<T>, service: InjectionToken<T>): void {
    this.container.bind(id).toService(service);
  }

  public get<T> (id: InjectionToken<T>): T {
    return this.container.get<T>(id);
  }

  public has<T> (id: InjectionToken<T>): boolean {
    return this.container.isBound(id);
  }

  public getParameterBag (): ParameterBagInterface {
    return this.parameterBag;
  }

  public getParameter<T> (name: string | symbol): T {
    return this.parameterBag.get<T>(name);
  }

  public hasParameter (name: string | symbol): boolean {
    return this.parameterBag.has(name);
  }

  public setParameter (name: string | symbol, value: unknown): void {
    this.parameterBag.set(name, value);
  }

  public getContainer (): InversifyContainer {
    return this.container;
  }

  public isCompiled (): boolean {
    return this.compiled;
  }

  public compile (): void {
    this.parameterBag.resolve();

    this.parameterBag = new FrozenParameterBag(this.parameterBag.all());

    for (const [ key, value ] of Object.entries(this.parameterBag.all())) {
      if (this.has(key)) {
        throw new Error(`Service with id ${String(key)} already exists. Cannot assign a parameter with this name`);
      }

      this.container.bind(key).toConstantValue(value);
    }

    this.compiled = true;
  }
}
